import React, {
  useRef,
  useCallback,
  useMemo,
  useEffect,
  useState,
  FC,
  ReactNode,
  ComponentProps,
} from 'react';

import PropTypes, { Validator } from 'prop-types';
import { VariableSizeList } from 'react-window';

import { useLocale } from '@adsk/alloy-react-locale';

import ProjectListAccountItem from './ProjectListAccountItem';
import ProjectListCategoryTitle from './ProjectListCategoryTitle';
import ProjectListProjectItem from './ProjectListProjectItem';
import ProjectListTemplateItem from './ProjectListTemplateItem';
import ProjectRowLoader from './ProjectRowLoader';
import ProjectsError from './ProjectsError';
import TemplateRowLoader from './TemplateRowLoader';
import { accountsListShape, projectsListShape } from '../helpers/propTypes';
import { Account, Project } from '../helpers/types';

const CATEGORY_HEADER_TYPE = 'CATEGORY_HEADER_TYPE';
const PROJECT_LOADER_TYPE = 'PROJECT_LOADER_TYPE';
const TEMPLATE_LOADER_TYPE = 'TEMPLATE_LOADER_TYPE';
const PROJECT_ERROR_TYPE = 'PROJECT_ERROR_TYPE';
const TEMPLATE_ERROR_TYPE = 'TEMPLATE_ERROR_TYPE';
const ACCOUNT_TYPE = 'ACCOUNT_TYPE';
const PROJECT_TYPE = 'PROJECT_TYPE';
const TEMPLATE_TYPE = 'TEMPLATE_TYPE';
const LIST_MAX_HEIGHT = 450;

type ItemType = {
  type: string;
};

type AccountItemType = {
  type: typeof ACCOUNT_TYPE;
} & Account;

type ProjectItemType = {
  type: typeof PROJECT_TYPE;
} & Project;

type TemplateItemType = {
  type: typeof TEMPLATE_TYPE;
  id: string;
};
type AllItemsTypes =
  | ProjectItemType
  | AccountItemType
  | TemplateItemType
  | ItemType;

type CategoryHeaderItem = ComponentProps<
  typeof ProjectListCategoryTitle
>['item'];

type CategoryHeaderItemType = {
  type: typeof CATEGORY_HEADER_TYPE;
} & CategoryHeaderItem;

type ListItemProps = {
  item: AllItemsTypes | CategoryHeaderItemType;
  index: unknown;
  currentAccountId?: string;
  currentProjectId?: string;
  currentProductId?: string;
  onClose?: () => void;
  projectsRetry?: () => void;
  projectsCount?: number;
  onAccountClick?: (account?: object, url?: string) => void;
  onProjectClick?: (project?: object, url?: string) => void;
  handleAnalyticOnClick?: (project?: object) => void;
};

const ListItem: FC<ListItemProps> = (props) => {
  const { item } = props;
  const type = item.type;
  switch (type) {
    case CATEGORY_HEADER_TYPE:
      return <ProjectListCategoryTitle item={item as CategoryHeaderItem} />;
    case PROJECT_LOADER_TYPE:
      return <ProjectRowLoader />;
    case TEMPLATE_LOADER_TYPE:
      return <TemplateRowLoader />;
    case PROJECT_ERROR_TYPE:
      return (
        <ProjectsError
          retry={props.projectsRetry}
          projectsCount={props.projectsCount}
        />
      );
    case TEMPLATE_ERROR_TYPE:
      return (
        <ProjectsError
          retry={props.projectsRetry}
          projectsCount={props.projectsCount}
          variant={ProjectsError.VARIANTS.TEMPLATES}
        />
      );
    case ACCOUNT_TYPE:
      return (
        <ProjectListAccountItem {...props} item={item as AccountItemType} />
      );
    case PROJECT_TYPE:
      return (
        <ProjectListProjectItem {...props} item={item as ProjectItemType} />
      );
    case TEMPLATE_TYPE:
      return (
        <ProjectListTemplateItem {...props} item={item as ProjectItemType} />
      );
    default:
      console.error('VirtualizedProjectList - Unknown item type: ', type);
      return null;
  }
};

ListItem.propTypes = {
  item: PropTypes.shape({
    type: PropTypes.string,
  }) as Validator<never>,
  projectsRetry: PropTypes.func as Validator<never>,
  projectsCount: PropTypes.number as Validator<never>,
  onAccountClick: PropTypes.func,
  onProjectClick: PropTypes.func,
};

const getDefaultSizeByType = (type: string) => {
  switch (type) {
    case CATEGORY_HEADER_TYPE:
      return 42;
    case PROJECT_LOADER_TYPE:
      return 240;
    case TEMPLATE_LOADER_TYPE:
      return 180;
    default:
      return 64;
  }
};

const getSizeCacheKey = (item: AllItemsTypes) => {
  const type = (item as { type: string }).type;
  switch (type) {
    case ACCOUNT_TYPE:
      return `${type}_${(item as Account).account_id}`;
    case PROJECT_TYPE:
    case TEMPLATE_TYPE:
      return `${type}_${(item as Project).id}`;
    default:
      return type;
  }
};

type SetSizeType = (
  item: AllItemsTypes,
  height?: number,
  index?: number,
) => void;

type SizeDetectorProps = {
  setSize: SetSizeType;
  item: ItemType;
  index?: number;
  children: ReactNode;
};

const SizeDetector: FC<SizeDetectorProps> = ({
  setSize,
  item,
  index,
  children,
}) => {
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const { height } = root.current?.getBoundingClientRect() || {};
    if (height !== getDefaultSizeByType(item.type)) {
      setSize(item, height, index);
    }
  }, [setSize, item, index]);
  return <div ref={root}>{children}</div>;
};

SizeDetector.propTypes = {
  setSize: PropTypes.func as Validator<SetSizeType>,
  index: PropTypes.number,
  item: PropTypes.object as Validator<ItemType>,
  children: PropTypes.node,
};

type VirtualizedProjectListProps = {
  accounts: Account[];
  currentAccountId?: string;
  projects: Project[];
  currentProjectId?: string;
  templates: Project[];
  currentProductId?: string;
  projectsLoading?: boolean;
  projectsError?: boolean;
  projectsRetry?: () => void;
  onClose?: () => void;
  onAccountClick?: (account?: object, url?: string) => void;
  onProjectClick?: (project?: object, url?: string) => void;
  handleAnalyticOnClick?: (project?: object, url?: string) => void;
};

const VirtualizedProjectList: FC<VirtualizedProjectListProps> = ({
  accounts,
  currentAccountId,
  projects,
  currentProjectId,
  templates,
  currentProductId,
  projectsLoading,
  projectsError,
  projectsRetry,
  onClose,
  onAccountClick,
  onProjectClick,
  handleAnalyticOnClick,
}) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  const sizeMap = useRef<Record<string, number | undefined>>({});
  const listRef = useRef<VariableSizeList | null>(null);
  const [_, triggerRerender] = useState();

  useEffect(() => {
    // Reset cache as the current visible item heights might be different.
    listRef.current?.resetAfterIndex(0);
  }, [accounts, projects, templates, projectsLoading]);

  const items = useMemo(() => {
    const allItems = [];
    if (accounts && accounts.length) {
      allItems.push({
        type: CATEGORY_HEADER_TYPE,
        title: T.translate('ACCOUNTS'),
      });

      accounts.forEach((account) => {
        allItems.push({
          ...account,
          type: ACCOUNT_TYPE,
        });
      });
    }

    const showProjectsHeader = (projects && projects.length) || projectsError;
    if (showProjectsHeader) {
      allItems.push({
        type: CATEGORY_HEADER_TYPE,
        key: 'projects',
        title: T.translate('PROJECTS'),
      });
    }

    if (projects && projects.length) {
      projects.forEach((project) => {
        allItems.push({
          ...project,
          type: PROJECT_TYPE,
        });
      });

      if (projectsLoading) {
        allItems.push({
          type: PROJECT_LOADER_TYPE,
          key: 'projects_loader',
        });
      }
    }

    if (projectsError) {
      allItems.push({
        type: PROJECT_ERROR_TYPE,
        key: 'projects_error',
      });
    }

    if (templates && templates.length) {
      allItems.push({
        type: CATEGORY_HEADER_TYPE,
        key: 'templates',
        title: T.translate('TEMPLATES'),
      });

      templates.forEach((template) => {
        allItems.push({
          ...template,
          type: TEMPLATE_TYPE,
        });
      });

      if (projectsLoading) {
        allItems.push({
          type: TEMPLATE_LOADER_TYPE,
          key: 'templates_loader',
        });
      } else if (projectsError) {
        allItems.push({
          type: TEMPLATE_ERROR_TYPE,
          key: 'templates_error',
        });
      }
    }

    return allItems;
  }, [T, projects, accounts, templates, projectsLoading, projectsError]);

  const getSizeByItem = useCallback((item: AllItemsTypes) => {
    const cacheKey = getSizeCacheKey(item);
    return sizeMap.current[cacheKey] || getDefaultSizeByType(item.type);
  }, []);

  const getSizeByIndex = useCallback(
    (index: number) => {
      return getSizeByItem(items[index]);
    },
    [items, getSizeByItem],
  );

  const setSize: SetSizeType = useCallback(
    (item, size, index) => {
      const cacheKey = getSizeCacheKey(item);
      const prevSize = getSizeByItem(item);
      if (prevSize !== size) {
        sizeMap.current = { ...sizeMap.current, [cacheKey]: size };
        index !== undefined && listRef.current?.resetAfterIndex(index, false);
        triggerRerender(undefined);
      }
    },
    [getSizeByItem],
  );

  const itemsTotalHeight = useMemo(() => {
    return items.reduce((acc, item) => acc + getSizeByItem(item), 0);
  }, [items, getSizeByItem]);

  return (
    <div
      css={[
        {
          minHeight: 120,
          maxHeight: LIST_MAX_HEIGHT,
          overflow: 'auto',
        },
      ]}
    >
      <VariableSizeList
        height={Math.min(LIST_MAX_HEIGHT, itemsTotalHeight)}
        width="100%"
        itemCount={items.length}
        itemSize={getSizeByIndex}
        itemData={items}
        ref={listRef}
      >
        {({ index, style }) => (
          // Using inline style explicitly to fix Styled Component warnings of creating the same style again and again
          <div style={style}>
            <SizeDetector setSize={setSize} index={index} item={items[index]}>
              <ListItem
                index={index}
                item={items[index]}
                currentAccountId={currentAccountId}
                currentProjectId={currentProjectId}
                currentProductId={currentProductId}
                onClose={onClose}
                projectsRetry={projectsRetry}
                projectsCount={projects.length}
                onAccountClick={onAccountClick}
                onProjectClick={onProjectClick}
                handleAnalyticOnClick={handleAnalyticOnClick}
              />
            </SizeDetector>
          </div>
        )}
      </VariableSizeList>
    </div>
  );
};

VirtualizedProjectList.displayName = 'VirtualizedProjectList';

VirtualizedProjectList.propTypes = {
  /** Available accounts to select from */
  accounts: accountsListShape,
  /** current selected account id */
  currentAccountId: PropTypes.string,
  /** Available projects to select from */
  projects: projectsListShape,
  /** Available templates */
  templates: projectsListShape,
  /** current selected project id */
  currentProjectId: PropTypes.string,
  /** current product ID, to check if project has entitlements for or not */
  currentProductId: PropTypes.string,
  /** show projects loader */
  projectsLoading: PropTypes.bool,
  /** is project entitlements ended in error */
  projectsError: PropTypes.bool,
  /** retry loading project entitlements */
  projectsRetry: PropTypes.func,
  /** menu close handler */
  onClose: PropTypes.func,
  /** account click handler */
  onAccountClick: PropTypes.func,
  /** project click handler */
  onProjectClick: PropTypes.func,
};

export default VirtualizedProjectList;
